import {
  ApplicationRef,
  ComponentFactoryResolver,
  ComponentRef,
  Injectable,
  Injector,
  OnDestroy,
} from '@angular/core';
import {
  MfAccountingServiceComponent,
  ServiceType,
} from '@web-frontend/microfrontends/mf-accounting-service/mf-accounting-service.component';
import {
  CompanyQuota,
  CheckTrialResponse,
  PermissionService,
} from '../permissions';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';

export enum SubscriptionUpdateReason {
  seats = 'seats',
  billing = 'billing_documents',
}

@Injectable({
  providedIn: 'root',
})
export class SubscriptionService implements OnDestroy {
  private componentRef: ComponentRef<MfAccountingServiceComponent> | null = null;

  constructor(
    private appRef: ApplicationRef,
    private injector: Injector,
    private cfr: ComponentFactoryResolver,
    private permissionService: PermissionService,
    private readonly router: Router,
    private readonly http: HttpClient
  ) {}

  destroy(): void {
    if (this.componentRef) {
      const element = (this.componentRef.hostView as any)
        .rootNodes[0] as HTMLElement;

      if (element && element.parentNode) {
        element.parentNode.removeChild(element);
      }

      this.appRef.detachView(this.componentRef.hostView);
      this.componentRef.destroy();
      this.componentRef = null;
    }
  }

  injectComponent(serviceType: ServiceType): void {
    this.destroy();

    const factory = this.cfr.resolveComponentFactory(
      MfAccountingServiceComponent
    );
    this.componentRef = factory.create(this.injector);

    this.componentRef.instance.mode = serviceType;
    this.componentRef.instance.openPlans = true;

    this.appRef.attachView(this.componentRef.hostView);
    const domElem = (this.componentRef.hostView as any)
      .rootNodes[0] as HTMLElement;
    document.body.appendChild(domElem);
  }

  ngOnDestroy(): void {
    this.destroy();
  }

  checkSubscriptionQuota(
    quotas: CompanyQuota[] = [],
    resource: SubscriptionUpdateReason
  ): boolean {
    const quota = quotas.find((q) => q.resource === resource);
    return !quota || !quota.limit || quota.limit > quota.current;
  }

  canAddSeats(): Promise<{ value: boolean; serviceType: ServiceType }> {
    const url = `:API_BASE_URLsubscriptions/subscriptions`;
    return this.http
      .get<{ quotas?: CompanyQuota[]; applicableFeatures?: string[] }>(url)
      .toPromise()
      .then(
        (res: { quotas: CompanyQuota[]; applicableFeatures?: string[] }) => {
          const canAddSeats = this.checkSubscriptionQuota(
            res?.quotas ?? [],
            SubscriptionUpdateReason.seats
          );

          let serviceType = ServiceType.addSeats;
          if (
            !(res.applicableFeatures || []).includes(
              SubscriptionUpdateReason.seats
            )
          ) {
            serviceType = ServiceType.regularPlans;
          }

          return {
            value: canAddSeats,
            serviceType: serviceType,
          };
        }
      )
      .catch(() => {
        return { value: true, serviceType: ServiceType.regularPlans };
      });
  }

  async checkBillingDocumentsQuota(): Promise<void> {
    const url = `:API_BASE_URLsubscriptions/subscriptions`;
    const result = await this.http
      .get<{ quotas?: CompanyQuota[] }>(url)
      .toPromise();

    const canAdd = this.checkSubscriptionQuota(
      result?.quotas ?? [],
      SubscriptionUpdateReason.billing
    );

    if (canAdd) return;

    this.router.navigateByUrl(
      'admin/pricing?updateReason=' + SubscriptionUpdateReason.billing
    );
  }

  async canAddPlanSeats(): Promise<boolean> {
    try {
      const { value, serviceType } = await this.canAddSeats();
      if (value) return true;
      if (serviceType === ServiceType.addSeats) {
        this.injectComponent(ServiceType.addSeats);
      } else {
        this.router.navigateByUrl(
          `admin/pricing?updateReason=${SubscriptionUpdateReason.seats}&fromUrl=${window.location.pathname}`
        );
      }

      return false;
    } catch {
      return false;
    }
  }

  async checkTrialSubscription(
    companyId: string,
    rootModule: string
  ): Promise<CheckTrialResponse> {
    if (!companyId || !rootModule) {
      return null;
    }

    try {
      const url = `:API_URL/subscription-manager/subscriptions/check-trial/${companyId}?rootModule=${rootModule}`;
      const response = await this.http.get<CheckTrialResponse>(url).toPromise();
      return response;
    } catch (error) {
      console.error('Error checking trial subscription:', error);
      return null;
    }
  }
}
